import {
  keepPreviousData, QueryClient, skipToken, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';
import authService from '../services/auth.service';
import riskService from '../services/risk.service';
import { getSeverityBands } from '../lib/severity';
import taskService from '../services/task.service';
import { CreateRiskData, RegisterApiParams, UpdateRiskData } from '../types/risk.types';
import { RegisterViewKey } from '../lib/registerViews';

export type { RegisterViewKey } from '../lib/registerViews';

const STALE_MS = 30_000;

export interface MatrixSlice {
  type: 'inherent' | 'residual';
  likelihood: number;
  impact: number;
}

export interface RegisterParams {
  view: RegisterViewKey;
  matrix?: MatrixSlice;
  search: string;
  ordering: string;
  page: number;
}

export function apiParamsFor(p: RegisterParams): RegisterApiParams {
  const params: RegisterApiParams = {
    ordering: p.ordering,
    page: p.page,
  };
  if (p.search) params.search = p.search;
  if (p.view === 'mine') params.my_risks = 'true';
  // The High floor is one above the Medium ceiling, read from the bands the
  // register badges with rather than written here. It was hardcoded to 12,
  // which was the old frontend floor, so the view quietly dropped the 10s and
  // 11s that the rows beside it label High.
  if (p.view === 'critical-high') {
    params.residual_score_min = getSeverityBands().medium + 1;
  }
  if (p.view === 'review-overdue') params.review_needed = 'true';
  // status pulls archived rows back in on its own (the backend treats asking
  // for the status as asking to see them), but include_archived is explicit
  // about the intent and survives someone changing that inference later.
  if (p.view === 'archived') {
    params.status = 'ARCHIVED';
    params.include_archived = 'true';
  }
  if (p.matrix) {
    params[`${p.matrix.type}_likelihood`] = p.matrix.likelihood;
    params[`${p.matrix.type}_impact`] = p.matrix.impact;
  }
  return params;
}

export function useRisksPage(p: RegisterParams) {
  // Keyed on the params actually sent, not on the inputs they are derived
  // from. The Critical & High floor now comes from the severity bands, which
  // useSeverityBands sets in module state after its own fetch resolves, so a
  // page fetched before the operator's real bands landed would otherwise keep
  // a default-derived floor under an unchanged key until staleTime expired.
  // Keying on the output makes every derived input part of the identity.
  const params = apiParamsFor(p);
  return useQuery({
    queryKey: ['risks-page', params],
    queryFn: () => riskService.getRisksPage(params),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useRiskDetail(riskId: string | null) {
  return useQuery({
    queryKey: ['risk-detail', riskId],
    queryFn: riskId == null ? skipToken : () => riskService.getRisk(riskId),
    staleTime: STALE_MS,
  });
}

export function useRiskTasks(riskId: string | null) {
  return useQuery({
    queryKey: ['risk-tasks', riskId],
    queryFn: riskId == null ? skipToken : () => taskService.getTasks({ risk_item: riskId }),
    staleTime: STALE_MS,
  });
}

// companyId scopes the fetch to that company's membership — the set of users
// who can own a risk there (see user_can_access_org_scope; the company role
// backfill makes "any role under the company" the assignable set at every
// org level). undefined = all users (inline reassignment pickers); null =
// an org-scoped picker with no org chosen yet, so the query stays disabled.
export function useUserOptions(companyId?: string | null) {
  const scope = companyId === undefined ? 'all' : companyId === null ? 'none' : companyId;
  return useQuery({
    queryKey: ['users-options', scope],
    queryFn: companyId === null
      ? skipToken
      : () => authService.getUsers(companyId ? { company: companyId, isActive: true } : { isActive: true }),
    staleTime: 300_000,
  });
}

// Prefix match on 'recent-activities' — covers every ['recent-activities', limit]
// the Journal mounts.
export function invalidateRiskFamilies(queryClient: QueryClient) {
  ['risks-page', 'risk-detail', 'risk-stats', 'risk-matrix', 'attention-tasks', 'attention-risks', 'recent-activities']
    .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
}

export function useUpdateRiskInline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRiskData }) => riskService.updateRisk(id, data),
    onSuccess: () => invalidateRiskFamilies(queryClient),
  });
}

export function useArchiveRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => riskService.archiveRisk(id),
    onSuccess: () => invalidateRiskFamilies(queryClient),
  });
}

export function useRestoreRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => riskService.restoreRisk(id),
    onSuccess: () => invalidateRiskFamilies(queryClient),
  });
}

export function useCreateRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRiskData) => riskService.createRisk(data),
    onSuccess: () => invalidateRiskFamilies(queryClient),
  });
}

export function useUpdateRisk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRiskData }) => riskService.updateRisk(id, data),
    onSuccess: () => invalidateRiskFamilies(queryClient),
  });
}
